<template>
  <!-- Video card -->
  <el-card class="clip-card box-card">
    <div slot="header" class="audioHeader">
      <audio-item-header
        :title="title"
        :status="status"
        :progress="progress"
        :is-removable="isRemovable"
        :percent-completed="percentCompleted"
        :sharings="videoSharings"
        :is-uploaded-to-mangomolo="audioInfo.isUploadedToMangomolo"
        :is-uploading-to-mangomolo="isUploadingToMangomolo"
        @updateTitle="$emit('updateTitle', $event)"
        @preview="$emit('preview')"
        @download="$emit('download')"
        @edit="showEditForm"
        @copy="$emit('copy')"
        @delete="$emit('delete')"
      />
    </div>

    <!-- Video information -->
    <audio-item-content
      class="clip-card__content"
      :app="app"
      :stream="stream"
      :start-time="startTime"
      :end-time="endTime"
      :duration="duration"
      :snapchat-link="snapchatLink"
      :upload-to-mangomolo="
        !audioInfo.isUploadedToMangomolo && !isUploadingToMangomolo
      "
      @uploadToMangomolo="uploadToMangomolo"
    />
    <sharing-from-mangomolo
      v-if="sharingMM"
      :channels="channels"
      :title="audioInfo.title"
      :multistep-id="audioInfo.id"
      :description-info="stream"
      @closed="sharingMM = false"
      @sharingToMM="sharingToMM"
    />
  </el-card>
</template>

<script>
import AudioItemHeader from "@/components/media/audio/AudioItemHeader.vue";
import AudioItemContent from "@/components/media/audio/AudioItemContent.vue";
import SharingFromMangomolo from "@/components/socials/share/SharingFromMangomolo.vue";

import { MEDIA_CONVERSION_STATUS, isMediaStatusValid } from "@/constants.js";
import { token } from "@/api/params.js";
import { mapState } from "vuex";

// @group Media/Audio
// Audio card
export default {
  name: "AudioItem",
  components: {
    AudioItemHeader,
    AudioItemContent,
    SharingFromMangomolo
  },
  data(){
    return {
      isUploadingToMangomolo: false,
      sharingMM: false
    }
  },
  computed: {
    ...mapState({
      currentChannel: state => state.userData.currentChannel,
      sharings: state => state.socials.sharings,
    }),
    // @vuese
    // Video sharings data
    videoSharings() {
      return this.sharings.filter(s => s.videoId == this.audioInfo.id);
    }
  },
  methods: {
    showEditForm() {
      this.$emit("stopPlayer");
      const route = this.$router.resolve({
        path: `/clipper/studio`,
        query: {
          token: token,
          duration: this.audioInfo?.metadata?.duration,
          // streamId: this.mediaItemsList.stream_id, ???????????
          stream: this.audioInfo.stream,
          thumbs: this.audioInfo.storageUrl,
          app: this.app,
          audioFlagFromClipper: true,
          videoInfoID: this.audioInfo.id,
          currentChannel: Number(this.currentChannel)
        }
      });
      window.open(route.href, "_blank");
    },
    sharingToMM(title, titleArabic, description, categoryVideoinfo, stream) {
      this.isUploadingToMangomolo = true;
      if (stream == undefined) {
        stream = this.stream;
      }

      this.$emit("uploadToMangomolo", {
        title,
        titleArabic,
        description,
        stream,
        categoryVideoinfo,
        done: () => {}
      });
    },
        // @vuese
    // Upload clip to Mangomolo service
    uploadToMangomolo() {
      this.sharingMM = true;
    },
  },
  watch: {
    audioInfo(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.audioInfo = newVal;
      }
      if (newVal.isUploadingToMangomolo !== this.isUploadingToMangomolo) {
        this.isUploadingToMangomolo = newVal.isUploadingToMangomolo;
      }
    },
  },
  props: {
    channels: {
      type: Array,
    },
    audioInfo: {
      type: Object
    },
    percentCompleted: {
      type: Number,
      default: 0
    },
    // Link to share clip to Snapchat
    snapchatLink: {
      type: String,
      default: null
    },
    mediaItemsList: {
      type: Object
    },
    /** Video clip data */
    title: {
      type: String,
      default: null
    },
    /** Audio conversion status */
    status: {
      type: String,
      default: MEDIA_CONVERSION_STATUS.READY,
      validator(val) {
        return isMediaStatusValid(val);
      }
    },
    /** Audio conversion percentage */
    progress: {
      type: Number,
      default: 0,
      validator(val) {
        return 0 <= val && val <= 100;
      }
    },
    /** Is it possible to delete audio */
    isRemovable: {
      type: Boolean,
      default: true
    },
    /** Audio start time */
    startTime: {
      type: Date,
      default: null
    },
    /** Audio end time */
    endTime: {
      type: Date,
      default: null
    },
    /** Wowza application */
    app: {
      type: String,
      default: "No Application"
    },
    /** Wowza stream name */
    stream: {
      type: String,
      default: "No Stream"
    },
    /** Audio actual duration in seconds. It may be useful when start & end
     *  are not valid because of conversion losses or just because it's can't
     *  be correctly calculated from those (as example - cutted clip) */
    duration: {
      type: Number,
      default: null
    }
  }
};
</script>

<style>
@keyframes load-sharing {
  from {
    width: 0px;
  }

  to {
    width: 20px;
  }
}
.share-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
}
.share-buttons a {
  text-decoration: none;
}
.remove-social-sharing-btn {
  cursor: pointer;
}

.watch-modal .modal {
  margin: 0 10px;
}
</style>

<style lang="scss" scoped>
::-webkit-input-placeholder {
  /* Chrome/Opera/Safari */
  color: #aaa;
}
::-moz-placeholder {
  /* Firefox 19+ */
  color: #aaa;
}
:-ms-input-placeholder {
  /* IE 10+ */
  color: #aaa;
}
:-moz-placeholder {
  /* Firefox 18- */
  color: #aaa;
}

.box-card {
  width: 100%;
  margin-bottom: 15px;
  overflow: hidden;
  background-color: #aaaaaa;
  justify-self: center;
}

.clip-card {
  &__content {
    padding: 10px;
  }
}

.audioHeader {
  display: flex;
  justify-content: space-between;
  text-align: center;
}

.el-card__header {
  padding: 10px !important;
}

.el-card__body {
  padding: 10px !important;
}
</style>
